import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { collection, query, where, getDocs, updateDoc, doc, arrayUnion, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";

export default function JoinLeague() {
  const { code } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [leagueName, setLeagueName] = useState("");
  const [leagueDocId, setLeagueDocId] = useState(null);

  useEffect(() => {
    async function findLeague() {
      try {
        const q = query(
          collection(db, "leagues"),
          where("inviteCode", "==", code.toUpperCase())
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setStatus("invalid");
          return;
        }

        const leagueDoc = snapshot.docs[0];
        const data = leagueDoc.data();
        setLeagueName(data.name);
        setLeagueDocId(leagueDoc.id);

        // Already a member
        if (data.members?.includes(currentUser.uid)) {
          setStatus("already_member");
          setTimeout(() => navigate(`/league/${leagueDoc.id}`), 1500);
          return;
        }

        // Already requested
        if (data.pendingMembers?.includes(currentUser.uid)) {
          setStatus("already_requested");
          return;
        }

        setStatus("found");
      } catch (err) {
        setStatus("error");
      }
    }
    findLeague();
  }, [code, currentUser, navigate]);

  async function handleRequestJoin() {
    try {
      await updateDoc(doc(db, "leagues", leagueDocId), {
        pendingMembers: arrayUnion(currentUser.uid),
      });
      setStatus("requested");
    } catch (err) {
      setStatus("error");
    }
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-20">
        <div className="card p-10 text-center">

          {status === "loading" && (
            <p className="font-condensed text-gray-400 text-xl tracking-wide">Finding league...</p>
          )}

          {status === "invalid" && (
            <>
              <div className="text-6xl mb-4">❌</div>
              <h2 className="font-display text-4xl tracking-wider mb-3" style={{ color: "#0A0B0F" }}>
                INVALID CODE
              </h2>
              <p className="font-condensed text-gray-400 tracking-wide text-lg mb-6">
                No league found with that invite code. Please check the link and try again.
              </p>
              <Link to="/leagues" className="btn-lime px-8 py-3 rounded-xl inline-block">
                BACK TO LEAGUES
              </Link>
            </>
          )}

          {status === "already_member" && (
            <>
              <div className="text-6xl mb-4">✅</div>
              <h2 className="font-display text-4xl tracking-wider mb-3" style={{ color: "#0A0B0F" }}>
                ALREADY A MEMBER
              </h2>
              <p className="font-condensed text-gray-400 tracking-wide text-lg">
                You're already in {leagueName}. Redirecting...
              </p>
            </>
          )}

          {status === "already_requested" && (
            <>
              <div className="text-6xl mb-4">⏳</div>
              <h2 className="font-display text-4xl tracking-wider mb-3" style={{ color: "#0A0B0F" }}>
                REQUEST PENDING
              </h2>
              <p className="font-condensed text-gray-400 tracking-wide text-lg mb-6">
                You've already requested to join <strong>{leagueName}</strong>. Waiting for the creator to approve.
              </p>
              <Link to="/leagues" className="btn-lime px-8 py-3 rounded-xl inline-block">
                BACK TO LEAGUES
              </Link>
            </>
          )}

          {status === "found" && (
            <>
              <div className="text-6xl mb-4">🏆</div>
              <p className="font-condensed text-gray-400 tracking-widest uppercase text-sm mb-2">
                Private League
              </p>
              <h2 className="font-display text-5xl tracking-wider mb-3" style={{ color: "#0A0B0F" }}>
                {leagueName.toUpperCase()}
              </h2>
              <p className="font-condensed text-gray-400 tracking-wide text-lg mb-8">
                Send a join request to the league creator. They'll need to approve it before you can see the leaderboard.
              </p>
              <button
                onClick={handleRequestJoin}
                className="btn-lime w-full py-4 rounded-xl text-xl mb-3"
              >
                REQUEST TO JOIN
              </button>
              <Link to="/leagues" className="font-condensed text-gray-400 tracking-wide text-sm hover:underline">
                Cancel
              </Link>
            </>
          )}

          {status === "requested" && (
            <>
              <div className="text-6xl mb-4">⏳</div>
              <h2 className="font-display text-4xl tracking-wider mb-3" style={{ color: "#0A0B0F" }}>
                REQUEST SENT!
              </h2>
              <p className="font-condensed text-gray-400 tracking-wide text-lg mb-6">
                Your request to join <strong>{leagueName}</strong> has been sent. You'll be able to see the leaderboard once the creator approves you.
              </p>
              <Link to="/leagues" className="btn-lime px-8 py-3 rounded-xl inline-block">
                BACK TO LEAGUES
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="font-display text-4xl tracking-wider mb-3" style={{ color: "#0A0B0F" }}>
                SOMETHING WENT WRONG
              </h2>
              <p className="font-condensed text-gray-400 tracking-wide text-lg mb-6">
                Please try again or go back to leagues.
              </p>
              <Link to="/leagues" className="btn-lime px-8 py-3 rounded-xl inline-block">
                BACK TO LEAGUES
              </Link>
            </>
          )}

        </div>
      </div>
    </div>
  );
}